import React, { useEffect, useMemo, useState } from "react";
import DropDownTab from "@src/components/DropDownTab";
import useDropDownTab from "@src/components/DropDownTab/useDropDownTab";
import MultiSelect from "@src/components/MultiSelect";
import { SearchInput } from "@src/components/SearchInput";
import { theme } from "@src/theme";
import {
  useFetchFeelingTagsQuery,
  useFetchGenresTagsQuery,
} from "@src/queries/tagQueries";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { ITag, ITagType } from "@src/types/tags";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  setCreateTagModalOpen,
  setDeleteTagState,
  setRefetchTagsByTagType,
  setTagTypeToCreate,
  setUpdateTagState,
  setTagFactoryFilters,
} from "@src/redux/slices/tagFactorySlice";
import { useRole } from "@src/hooks/useAuth";

const schema = yup.object().shape({
  searchText: yup.string().required(),
});
const TagSection = ({ tagType }: { tagType: ITagType }) => {
  const { watch, register } = useForm({
    defaultValues: { searchText: "" },
    resolver: yupResolver(schema),
  });
  const { isCreateModalOpen, refetchTagsByTagType, filters } = useAppSelector(
    (state) => state.tagFactory
  );
  const dispatch = useAppDispatch();
  const role = useRole();
  const isAdminOrManager = role === "admin" || role === "manager";
  const fetchTagsQueryMap = {
    genre: useFetchGenresTagsQuery,
    feeling: useFetchFeelingTagsQuery,
  };

  const fetchTagsQuery = useMemo(() => fetchTagsQueryMap[tagType], [tagType]);
  const { data: tagsResponse, refetch } = fetchTagsQuery({
    searchText: watch("searchText"),
  });
  useEffect(() => {
    if (
      tagType === refetchTagsByTagType &&
      refetchTagsByTagType !== undefined
    ) {
      refetch();
      dispatch(setRefetchTagsByTagType(undefined));
    }
  }, [refetchTagsByTagType]);
  const {
    closeDropdown: closeTagSection,
    isOpen: isTagSectionOpen,
    toggleDropdown: toggleTagSection,
    openDropdown: openTagSection,
  } = useDropDownTab();
  // Initialize selectedTags from Redux state
  const initialSelectedTags = useMemo(() => {
    if (tagType === "genre") return filters.genre || [];
    if (tagType === "feeling") return filters.emotion || [];
    return [];
  }, [tagType, filters]);

  const [selectedTags, setSelectedTags] = useState<ITag[]>(initialSelectedTags);

  // Sync local state with Redux when filters are reset
  useEffect(() => {
    if (tagType === "genre") {
      setSelectedTags(filters.genre || []);
    } else if (tagType === "feeling") {
      setSelectedTags(filters.emotion || []);
    }
  }, [filters.genre, filters.emotion, tagType]);

  // Update Redux when selectedTags change
  useEffect(() => {
    if (tagType === "genre") {
      dispatch(setTagFactoryFilters({ genre: selectedTags }));
    } else if (tagType === "feeling") {
      dispatch(setTagFactoryFilters({ emotion: selectedTags }));
    }
  }, [selectedTags, tagType, dispatch]);

  const tags = useMemo(() => {
    return (
      (tagsResponse as unknown as AxiosResponse<ApiResponse<ITag[]>>)?.data
        ?.data || []
    );
  }, [tagsResponse]);

  const toggleCreateModal = () => {
    dispatch(setTagTypeToCreate(tagType));
    dispatch(setCreateTagModalOpen(!isCreateModalOpen));
  };

  const deleteTagAction = (option: ITag) => {
    dispatch(
      setDeleteTagState({
        modalOpen: true,
        tagData: option,
        tagType,
      })
    );
    // selectedTags.includes(option) && setSelectedTags(selectedTags.filter(tag => tag !== option));
  };
  const updateTagAction = (option: ITag) => {
    dispatch(
      setUpdateTagState({
        modalOpen: true,
        tagData: option,
        tagType,
      })
    );
  };

  return (
    <DropDownTab
      contentContainerStyles={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: theme.margins.medium,
        backgroundColor: theme.colors.gray300,
      }}
      isOpen={isTagSectionOpen}
      toggleDropdown={toggleTagSection}
      closeDropdown={closeTagSection}
      openDropdown={openTagSection}
      title={`${tagType.charAt(0).toUpperCase() + tagType.slice(1)} tags`}
      key={1}
    >
      <div>
        <div
          style={{
            paddingBottom: theme.margins.medium,
          }}
        >
          <SearchInput
            placeholder="Search by name"
            {...register("searchText")}
          />
        </div>
        <MultiSelect<ITag>
          toggleCreateModal={isAdminOrManager ? toggleCreateModal : undefined}
          optionEntityName="tag"
          options={tags}
          labelKey={"title"}
          valueKey={"documentId"}
          setSelected={setSelectedTags}
          selected={selectedTags}
          enableCreate={isAdminOrManager}
          menuItems={
            isAdminOrManager
              ? [
                  {
                    label: "Delete",
                    onClick: deleteTagAction,
                  },
                  {
                    label: "Edit",
                    onClick: updateTagAction,
                  },
                ]
              : []
          }
        />
      </div>
    </DropDownTab>
  );
};

export default TagSection;
